import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Circle, ArrowRight, Rocket } from "lucide-react";

const LS_DISMISSED = "threec_tour_dismissed";
const LS_AI_RAN = "threec_tour_ai_ran";
const LS_PDF_EXPORTED = "threec_tour_pdf_exported";

export function markAiRan() {
  try {
    localStorage.setItem(LS_AI_RAN, "1");
  } catch (_e) {}
}
export function markPdfExported() {
  try {
    localStorage.setItem(LS_PDF_EXPORTED, "1");
  } catch (_e) {}
}

export default function OnboardingChecklist({ projectExists }) {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(LS_DISMISSED) === "1"
  );
  const [aiRan, setAiRan] = useState(
    () => localStorage.getItem(LS_AI_RAN) === "1"
  );
  const [pdfExported, setPdfExported] = useState(
    () => localStorage.getItem(LS_PDF_EXPORTED) === "1"
  );

  useEffect(() => {
    const t = setInterval(() => {
      setAiRan(localStorage.getItem(LS_AI_RAN) === "1");
      setPdfExported(localStorage.getItem(LS_PDF_EXPORTED) === "1");
    }, 2000);
    return () => clearInterval(t);
  }, []);

  const steps = [
    {
      id: "project",
      label: "Add your first live project",
      done: !!projectExists,
      cta: null,
    },
    {
      id: "ai",
      label: "Run your first AI module",
      done: aiRan,
      cta: { to: "/contract", text: "Open Contract AI" },
    },
    {
      id: "pdf",
      label: "Export a PDF report",
      done: pdfExported,
      cta: { to: "/finance", text: "Open Finance AI" },
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;

  if (dismissed || allDone) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(LS_DISMISSED, "1");
    } catch (_e) {}
  };

  return (
    <div
      data-testid="onboarding-checklist"
      className="relative border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 via-slate-950/60 to-slate-950/0 rounded-sm p-4"
    >
      <button
        data-testid="onboarding-dismiss"
        onClick={dismiss}
        className="absolute top-2 right-2 h-6 w-6 grid place-items-center text-slate-500 hover:text-slate-200"
        aria-label="Dismiss onboarding checklist"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-center gap-2 mb-3">
        <Rocket className="h-4 w-4 text-cyan-400" />
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">
            Get started
          </div>
          <div className="font-display text-sm font-semibold text-slate-100">
            3-step first-run · {doneCount}/{steps.length} complete
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {steps.map((s, idx) => (
          <div
            key={s.id}
            data-testid={`onboarding-step-${s.id}`}
            className={`flex items-center gap-2 rounded-sm border px-3 py-2 ${
              s.done
                ? "border-emerald-500/40 bg-emerald-500/5"
                : "border-slate-800 bg-slate-900/40"
            }`}
          >
            <div
              className={`h-5 w-5 grid place-items-center rounded-full shrink-0 ${
                s.done
                  ? "bg-emerald-500 text-slate-950"
                  : "border border-slate-700 text-slate-500"
              }`}
            >
              {s.done ? (
                <Check className="h-3 w-3" strokeWidth={3} />
              ) : (
                <span className="text-[10px] font-mono">{idx + 1}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className={`text-xs truncate ${
                  s.done ? "text-slate-400 line-through" : "text-slate-100"
                }`}
              >
                {s.label}
              </div>
            </div>
            {!s.done && s.cta ? (
              <Link
                to={s.cta.to}
                data-testid={`onboarding-cta-${s.id}`}
                className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.15em] text-cyan-300 hover:text-cyan-200"
              >
                {s.cta.text}
                <ArrowRight className="h-3 w-3" />
              </Link>
            ) : null}
            {!s.done && !s.cta ? (
              <Circle className="h-3 w-3 text-slate-600" />
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
